import * as React from 'react';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastAction;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface ToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastAction;
  open?: boolean;
}

const ACTION_TYPES = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  UPDATE_TOASTS: 'UPDATE_TOASTS',
} as const;

type ActionType = typeof ACTION_TYPES;

type Action =
  | { type: ActionType['ADD_TOAST']; toast: Toast }
  | { type: ActionType['UPDATE_TOAST']; toast: Partial<Toast> }
  | { type: ActionType['DISMISS_TOAST']; toastId?: string }
  | { type: ActionType['UPDATE_TOASTS']; toasts: Toast[] };

type State = {
  toasts: Toast[];
};

const toastTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

const addToasts = (state: State, toast: Toast) => {
  const newToast: Toast = {
    ...toast,
    id: new Date().getTime() + Math.random().toString(),
    open: true,
  };
  return { ...state, toasts: [...state.toasts, newToast] };
};

const dismissToast = (state: State, { toastId }: { toastId?: string }) => {
  if (toastId) {
    clearTimeout(toastTimeouts[toastId]);
    delete toastTimeouts[toastId];
  }
  return {
    ...state,
    toasts: state.toasts.map((t) =>
      t.id === toastId ? { ...t, open: false } : t
    ),
  };
};

const initialState: State = {
  toasts: [],
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ADD_TOAST':
      return addToasts(state, action.toast);
    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };
    case 'DISMISS_TOAST':
      return dismissToast(state, action);
    case 'UPDATE_TOASTS':
      return { ...state, toasts: action.toasts };
    default:
      return state;
  }
}

const listeners: Array<() => void> = [];

let memoryState: State = initialState;

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener();
  });
}

const TOAST_LIMIT = 1;
let count = 0;

export function toast({ ...props }: ToastOptions) {
  const id = String(++count);

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) {
          dispatch({ type: 'DISMISS_TOAST', toastId: id });
        }
      },
    },
  });

  setTimeout(() => {
    dispatch({ type: 'DISMISS_TOAST', toastId: id });
  }, 5000);

  return {
    id,
    dismiss: () => dispatch({ type: 'DISMISS_TOAST', toastId: id }),
  };
}

function useSafeSetState(dispatch: React.Dispatch<Action>) {
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return React.useCallback(
    (action: Action) => {
      if (mountedRef.current) {
        dispatch(action);
      }
    },
    [dispatch],
  );
}

interface ToastContextType {
  toasts: Toast[];
  dismiss: (toastId?: string) => void;
  toast: (options: ToastOptions) => { id: string; dismiss: () => void };
  updateToast: (id: string, updates: Partial<Toast>) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const dispatchWithTimeout = useSafeSetState(dispatch);

  const { toasts } = state;

  React.useEffect(() => {
    if (toasts.length > TOAST_LIMIT) {
      const dismissDelay = 1000;
      const toastsToDismiss = toasts.slice(0, toasts.length - TOAST_LIMIT);
      toastsToDismiss.forEach((toast) => {
        setTimeout(() => {
          dispatchWithTimeout({
            type: 'DISMISS_TOAST',
            toastId: toast.id,
          });
        }, dismissDelay);
      });
    }
  }, [toasts, dispatchWithTimeout]);

  const handleDismiss = React.useCallback(
    (toastId?: string) => {
      dispatchWithTimeout({
        type: 'DISMISS_TOAST',
        toastId,
      });
    },
    [dispatchWithTimeout],
  );

  return (
    <ToastContext.Provider
      value={{
        ...state,
        dismiss: handleDismiss,
        toast,
        updateToast: (id, updates) => {
          dispatchWithTimeout({
            type: 'UPDATE_TOAST',
            toast: { id, ...updates },
          });
        },
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

function useToast() {
  const context = React.useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export { useToast, ToastContext };
